import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React from "react";

export const useResourceList = () => {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const filters = React.useMemo(
    () => searchParams.getAll("filter").filter(Boolean),
    [searchParams]
  );

  const page = React.useMemo(() => {
    const n = Number(searchParams.get("page"));
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }, [searchParams]);

  const sort = React.useMemo(() => searchParams.get("sort"), [searchParams]);

  const commit = React.useCallback(
    (params: URLSearchParams) => {
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [pathname, router]
  );

  const pageSet = React.useCallback(
    (next: number) => {
      if (next < 1) {
        return;
      }
      const params = new URLSearchParams(searchParams.toString());

      if (next === 1) {
        params.delete("page");
      } else {
        params.set("page", String(next));
      }

      commit(params);
    },
    [searchParams, commit]
  );

  const filtersSet = React.useCallback(
    (nextTags: string[]) => {
      const params = new URLSearchParams(searchParams.toString());

      params.delete("filter");

      nextTags.forEach((tag) => {
        if (tag) {
          params.append("filter", tag);
        }
      });

      commit(params);
    },
    [searchParams, commit]
  );

  const sortSet = React.useCallback(
    (next: string | null) => {
      const params = new URLSearchParams(searchParams.toString());

      if (next === null) {
        params.delete("sort");
      } else {
        params.set("sort", next);
      }

      commit(params);
    },
    [searchParams, commit]
  );

  return { filters, filtersSet, page, pageSet, sort, sortSet };
};
